import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Select,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  Modal,
  Timeline,
  Descriptions,
  Statistic,
  Spin,
  Empty,
  message,
  Divider,
} from 'antd';
import {
  AuditOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  EyeOutlined,
  HistoryOutlined,
  TeamOutlined,
  SwapOutlined,
  PlusCircleOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EditOutlined,
  GlobalOutlined,
  LaptopOutlined,
  FieldTimeOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import axiosConfig from '../../../../../config/axiosConfig';
import './AdminDashboard.css';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// ─── Color map for action types (colors match the spec) ──────────────────────
const ACTION_CONFIG = {
  USER_CREATED:        { color: 'green',  icon: <PlusCircleOutlined />,  label: 'User Created' },
  USER_DEACTIVATED:    { color: 'red',    icon: <StopOutlined />,        label: 'User Deactivated' },
  USER_REACTIVATED:    { color: 'blue',   icon: <CheckCircleOutlined />, label: 'User Reactivated' },
  ROLE_ASSIGNED:       { color: 'purple', icon: <SafetyOutlined />,      label: 'Role Assigned' },
  ROLE_CHANGED:        { color: 'orange', icon: <SwapOutlined />,        label: 'Role Changed' },
  PERMISSIONS_UPDATED: { color: 'cyan',   icon: <EditOutlined />,        label: 'Permissions Updated' },
};

// Ant Design Tag color name → CSS hex (for stat cards)
const TAG_COLOR_HEX = {
  green: '#10b981', red: '#ef4444', blue: '#4f6ef7',
  purple: '#8b5cf6', orange: '#f59e0b', cyan: '#06b6d4', default: '#9ca3af',
};

// Returns config; if API already provides actionLabel, that takes priority in the render.
const getActionConfig = (type) =>
  ACTION_CONFIG[type] || { color: 'default', icon: <AuditOutlined />, label: type || 'Unknown' };

// ─── Component ─────────────────────────────────────────────────────────────────
const AuditLogs = () => {
  // ── State ────────────────────────────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });

  // Filters
  const [actionTypeFilter, setActionTypeFilter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);

  // Action types dropdown options (from API)
  const [actionTypes, setActionTypes] = useState([]);
  const [actionTypesLoading, setActionTypesLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Detail modals
  const [detailModal, setDetailModal] = useState({ visible: false, log: null });
  const [userHistoryModal, setUserHistoryModal] = useState({ visible: false, data: [], loading: false, name: '' });
  const [adminActionsModal, setAdminActionsModal] = useState({ visible: false, data: [], loading: false, name: '' });
  const [roleHistoryModal, setRoleHistoryModal] = useState({ visible: false, data: [], loading: false, name: '' });

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ── Window resize ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Fetch action types for filter dropdown ───────────────────────────────────
  const fetchActionTypes = useCallback(async () => {
    setActionTypesLoading(true);
    try {
      const res = await axiosConfig.get('/admin/audit-logs/action-types');
      const atSuccess = res.data?.status || res.data?.success;
      const atData = res.data?.entity || res.data?.data;
      if (atSuccess && Array.isArray(atData)) {
        setActionTypes(atData);
      }
    } catch {
      // Fallback to hardcoded list
      setActionTypes(
        Object.entries(ACTION_CONFIG).map(([key, cfg]) => ({
          key,
          label: cfg.label,
          description: '',
        }))
      );
    } finally {
      setActionTypesLoading(false);
    }
  }, []);

  // ── Fetch statistics ─────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axiosConfig.get('/admin/audit-logs/stats', { params: { days: 30 } });
      if (res.data?.status || res.data?.success) {
        setStats(res.data?.entity || res.data?.data);
      }
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // ── Fetch logs (main table) ──────────────────────────────────────────────────
  const fetchLogs = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const body = { page, pageSize };
      if (actionTypeFilter) body.actionType = actionTypeFilter;
      if (searchText.trim()) body.search = searchText.trim();
      if (dateRange && dateRange[0] && dateRange[1]) {
        body.startDate = dateRange[0].toISOString();
        body.endDate = dateRange[1].toISOString();
      }

      const res = await axiosConfig.post('/admin/audit-logs', body);
      if (res.data?.status || res.data?.success) {
        const entity = res.data?.entity || res.data?.data || {};
        const apiLogs = entity.logs || [];
        const pag = entity.pagination || {};
        setLogs(apiLogs);
        setPagination({
          page: pag?.page || page,
          pageSize: pag?.pageSize || pageSize,
          total: pag?.total || 0,
          totalPages: pag?.totalPages || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      message.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [actionTypeFilter, searchText, dateRange]);

  // ── Init ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchActionTypes();
    fetchStats();
  }, [fetchActionTypes, fetchStats]);

  useEffect(() => {
    fetchLogs(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTypeFilter, dateRange]);

  // ── User audit history (actions ON a user) ───────────────────────────────────
  const fetchUserHistory = async (companyId, name) => {
    setUserHistoryModal({ visible: true, data: [], loading: true, name });
    try {
      const res = await axiosConfig.get(`/admin/audit-logs/user/${companyId}`, { params: { limit: 50 } });
      if (res.data?.status || res.data?.success) {
        const uhData = res.data?.entity?.logs || res.data?.entity || res.data?.data || [];
        setUserHistoryModal(prev => ({ ...prev, data: Array.isArray(uhData) ? uhData : [], loading: false }));
      } else {
        setUserHistoryModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      message.error('Failed to load user history');
      setUserHistoryModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ── Admin actions (actions BY an admin) ──────────────────────────────────────
  const fetchAdminActions = async (companyId, name) => {
    setAdminActionsModal({ visible: true, data: [], loading: true, name });
    try {
      const res = await axiosConfig.get(`/admin/audit-logs/admin-actions/${companyId}`, { params: { limit: 50 } });
      if (res.data?.status || res.data?.success) {
        const aaData = res.data?.entity?.logs || res.data?.entity || res.data?.data || [];
        setAdminActionsModal(prev => ({ ...prev, data: Array.isArray(aaData) ? aaData : [], loading: false }));
      } else {
        setAdminActionsModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      message.error('Failed to load admin actions');
      setAdminActionsModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ── Role change history ──────────────────────────────────────────────────────
  const fetchRoleHistory = async (companyId, name) => {
    setRoleHistoryModal({ visible: true, data: [], loading: true, name });
    try {
      const res = await axiosConfig.get(`/admin/audit-logs/role-history/${companyId}`);
      if (res.data?.status || res.data?.success) {
        const rhData = res.data?.entity?.logs || res.data?.entity || res.data?.data || [];
        setRoleHistoryModal(prev => ({ ...prev, data: Array.isArray(rhData) ? rhData : [], loading: false }));
      } else {
        setRoleHistoryModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      message.error('Failed to load role history');
      setRoleHistoryModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ── Handle search on Enter ───────────────────────────────────────────────────
  const handleSearch = () => {
    fetchLogs(1, pagination.pageSize);
  };

  // ── Clear all filters ────────────────────────────────────────────────────────
  const handleClearFilters = () => {
    setActionTypeFilter(null);
    setSearchText('');
    setDateRange(null);
    // fetchLogs will re-run via useEffect on filter change
  };

  // ── Refresh ──────────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    fetchLogs(pagination.page, pagination.pageSize);
    fetchStats();
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: 'descend',
      render: (val) => {
        const d = new Date(val);
        return (
          <Tooltip title={d.toLocaleString()}>
              <Space size={2} direction="vertical" style={{ gap: 0 }}>
              <Space size={3}>
                <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 10 }} />
                <Text style={{ fontSize: 11.5, fontWeight: 400 }}>
                  {d.toLocaleDateString()}
                </Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 10.5, paddingLeft: 14, lineHeight: '1.2' }}>
                {d.toLocaleTimeString()}
              </Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'actionType',
      key: 'actionType',
      width: 220,
      filters: actionTypes.map(t => ({ text: t.label, value: t.key })),
      onFilter: (value, record) => record.actionType === value,
      render: (type, record) => {
        const cfg = getActionConfig(type);
        const label = record.actionLabel || cfg.label;
        return (
          <Tag icon={cfg.icon} color={cfg.color} style={{ fontSize: 11, whiteSpace: 'normal', lineHeight: '17px', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Performed By',
      key: 'performedBy',
      width: 180,
      render: (_, record) => {
        const actor = record.performedBy;
        if (!actor) return <Text type="secondary">System</Text>;
        return (
          <Space size={4}>
            <div style={{
              width: 24, height: 24, borderRadius: '50%',
              background: 'linear-gradient(135deg, #4f6ef7, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 10, fontWeight: 500,
            }}>
              {(actor.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 400, fontSize: 12, lineHeight: '15px' }}>{actor.name || 'Unknown'}</div>
              <Text
                type="secondary"
                style={{ fontSize: 10.5, cursor: 'pointer', lineHeight: '1.2' }}
                onClick={() => fetchAdminActions(actor.companyId, actor.name)}
              >
                View actions →
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Target User',
      key: 'target',
      width: 180,
      render: (_, record) => {
        const target = record.target;
        if (!target) return <Text type="secondary">N/A</Text>;
        return (
          <Space size={4}>
            <div style={{
              width: 24, height: 24, borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981, #06b6d4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 10, fontWeight: 500,
            }}>
              {(target.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 400, fontSize: 12, lineHeight: '15px' }}>{target.name || 'Unknown'}</div>
              <Text
                type="secondary"
                style={{ fontSize: 10.5, cursor: 'pointer', lineHeight: '1.2' }}
                onClick={() => fetchUserHistory(target.companyId, target.name)}
              >
                View history →
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'actionDescription',
      key: 'actionDescription',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text style={{ fontSize: 12 }}>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => setDetailModal({ visible: true, log: record })}
            />
          </Tooltip>
          {record.target?.companyId && (
            <Tooltip title="Role History">
              <Button
                type="text"
                size="small"
                icon={<HistoryOutlined style={{ color: '#722ed1' }} />}
                onClick={() => fetchRoleHistory(record.target.companyId, record.target.name)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // ── Render stat cards ────────────────────────────────────────────────────────
  const renderStatCards = () => {
    if (!stats) return null;
    const { byActionType = {}, totals = {} } = stats;
    const statCards = [
      { title: 'Total Logs', value: totals.all || 0, color: '#4f6ef7', icon: <AuditOutlined /> },
      { title: 'Last 24 Hours', value: totals.last24Hours || 0, color: '#10b981', icon: <ClockCircleOutlined /> },
      { title: 'Last 7 Days', value: totals.last7Days || 0, color: '#8b5cf6', icon: <FieldTimeOutlined /> },
    ];

    // Add top action types
    const actionEntries = Object.entries(byActionType).sort((a, b) => b[1] - a[1]).slice(0, 3);

    return (
      <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
        {statCards.map((s, i) => (
          <Col xs={24} sm={8} md={6} lg={4} key={i}>
            <div className="admin-stat-card audit-stat-card admin-animate-in">
              <div className="admin-stat-icon" style={{ background: `${s.color}12`, color: s.color }}>
                {s.icon}
              </div>
              <div className="admin-stat-content">
                <div className="admin-stat-label">{s.title}</div>
                <div className="admin-stat-value" style={{ color: s.color }}>{s.value}</div>
              </div>
            </div>
          </Col>
        ))}
        {actionEntries.map(([key, count], i) => {
          const cfg = getActionConfig(key);
          const hex = TAG_COLOR_HEX[cfg.color] || TAG_COLOR_HEX.default;
          return (
            <Col xs={24} sm={8} md={6} lg={4} key={`a-${i}`}>
              <div className="admin-stat-card audit-stat-card admin-animate-in">
                <div className="admin-stat-icon" style={{ background: `${hex}12`, color: hex }}>
                  {cfg.icon}
                </div>
                <div className="admin-stat-content">
                  <div className="admin-stat-label">{cfg.label}</div>
                  <div className="admin-stat-value" style={{ color: hex }}>{count}</div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  // ── Render a single value object (old or new) ────────────────────────────────
  const renderValueBlock = (obj, isNew) => {
    if (!obj) return null;
    if (typeof obj === 'string') {
      return <span style={{ fontSize: 12 }}>{obj}</span>;
    }

    const bgStyle = isNew
      ? { background: '#f6ffed', border: '1px solid #b7eb8f' }
      : { background: '#fff1f0', border: '1px solid #ffa39e' };

    // Destructure all known fields from the API response shapes
    const {
      companyid: _companyid, roleid: _roleid,
      companyname, rolename, company_type,
      pages, permissions,
      // Contact-type fields
      username, mobileno, emailid, countrycode, bookid: _bookid,
      ...rest
    } = obj;

    // Detect if this is a contact entry (has mobileno / username but no pages)
    const isContactEntry = (mobileno || username) && !pages;

    return (
      <div style={{ ...bgStyle, borderRadius: 6, padding: '6px 10px', fontSize: 11.5, marginTop: 3, lineHeight: '1.35' }}>
        {/* Contact-type entry */}
        {isContactEntry && (
          <>
            {username && (
              <div><Text type="secondary">Name: </Text><strong>{username}</strong></div>
            )}
            {mobileno && (
              <div style={{ marginTop: 2 }}>
                <Text type="secondary">Mobile: </Text>
                <span>{countrycode ? `+${countrycode} ` : ''}{mobileno}</span>
              </div>
            )}
            {emailid && (
              <div style={{ marginTop: 2 }}>
                <Text type="secondary">Email: </Text><span>{emailid}</span>
              </div>
            )}
          </>
        )}

        {/* User / role entry */}
        {!isContactEntry && (
          <>
            {companyname && (
              <div><Text type="secondary">Name: </Text><strong>{companyname}</strong></div>
            )}
            {(rolename || company_type) && (
              <div style={{ marginTop: 2 }}>
                <Text type="secondary">Role: </Text>
                <Tag color={isNew ? 'blue' : 'default'} style={{ fontSize: 11, marginLeft: 2 }}>
                  {rolename || company_type}
                </Tag>
              </div>
            )}
            {emailid && (
              <div style={{ marginTop: 2 }}>
                <Text type="secondary">Email: </Text><span>{emailid ?? '—'}</span>
              </div>
            )}
            {Array.isArray(pages) && pages.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">Pages: </Text>
                {pages.map(p => (
                  <Tag key={p} color={isNew ? 'geekblue' : 'default'} style={{ fontSize: 11, marginRight: 2 }}>
                    {p}
                  </Tag>
                ))}
              </div>
            )}
            {permissions && typeof permissions === 'object' && Object.keys(permissions).length > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">Permissions: </Text>
                {Object.entries(permissions).map(([page, perm]) => (
                  <Tag
                    key={page}
                    color={perm === 'write' ? (isNew ? 'green' : 'default') : (isNew ? 'orange' : 'default')}
                    style={{ fontSize: 11, marginRight: 2, marginTop: 2 }}
                  >
                    {page}: {perm}
                  </Tag>
                ))}
              </div>
            )}
          </>
        )}

        {/* Any remaining unrecognised fields */}
        {Object.entries(rest).map(([k, v]) => v !== null && v !== undefined ? (
          <div key={k} style={{ marginTop: 2 }}>
            <Text type="secondary">{k}: </Text>
            <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
          </div>
        ) : null)}
      </div>
    );
  };

  // ── Render change diff (old → new) ──────────────────────────────────────────
  const renderChangeDiff = (oldVal, newVal) => {
    if (!oldVal && !newVal) {
      return <Text type="secondary" style={{ fontSize: 13 }}>No change data recorded.</Text>;
    }
    return (
      <div>
        {oldVal && (
          <div style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 11, fontWeight: 500 }}>⬅ Before</Text>
            {renderValueBlock(oldVal, false)}
          </div>
        )}
        {newVal && (
          <div>
            <Text style={{ fontSize: 11, fontWeight: 500 }}>➡ After</Text>
            {renderValueBlock(newVal, true)}
          </div>
        )}
      </div>
    );
  };

  // ── Timeline renderer (shared by user history & admin actions modals) ───────
  const renderTimeline = (data) => {
    if (!data || data.length === 0) {
      return <Empty description="No records found" />;
    }
    return (
      <Timeline style={{ marginTop: 16 }}>
        {data.map((item, i) => {
          const cfg = getActionConfig(item.actionType);
          return (
            <Timeline.Item
              key={item.auditId || i}
              color={cfg.color === 'green' ? 'green' : cfg.color === 'red' ? 'red' : cfg.color === 'blue' ? 'blue' : 'gray'}
              dot={cfg.icon}
            >
              <div style={{ marginBottom: 4 }}>
                <Tag color={cfg.color} style={{ fontSize: 11 }}>
                  {item.actionLabel || cfg.label}
                </Tag>
                <Text type="secondary" style={{ fontSize: 10.5, marginLeft: 6 }}>
                  {new Date(item.createdAt).toLocaleString()}
                </Text>
              </div>
              <Text style={{ fontSize: 12 }}>{item.actionDescription}</Text>
              {item.performedBy && (
                <div style={{ marginTop: 1 }}>
                  <Text type="secondary" style={{ fontSize: 10.5 }}>
                    By: {item.performedBy.name || 'System'}
                  </Text>
                </div>
              )}
              {item.target && (
                <div>
                  <Text type="secondary" style={{ fontSize: 10.5 }}>
                    On: {item.target.name || 'Unknown'}
                  </Text>
                </div>
              )}
            </Timeline.Item>
          );
        })}
      </Timeline>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Stats */}
      <Spin spinning={statsLoading}>{renderStatCards()}</Spin>

      {/* Filters */}
      <div className="admin-toolbar audit-toolbar">
        <div className="audit-filter-row">
          <div className="audit-filter-item audit-filter-search">
            <Input
              placeholder="Search descriptions..."
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              size="middle"
            />
          </div>
          <div className="audit-filter-item audit-filter-type">
            <Select
              placeholder="Action Type"
              value={actionTypeFilter}
              onChange={(val) => setActionTypeFilter(val)}
              allowClear
              loading={actionTypesLoading}
              style={{ width: '100%' }}
              size="middle"
            >
              {actionTypes.map((t) => {
                const cfg = getActionConfig(t.key);
                return (
                  <Option key={t.key} value={t.key}>
                    <Space size={4}>
                      {cfg.icon}
                      <span>{t.label}</span>
                    </Space>
                  </Option>
                );
              })}
            </Select>
          </div>
          <div className="audit-filter-item audit-filter-date">
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              style={{ width: '100%' }}
              size="middle"
              format="YYYY-MM-DD"
              popupClassName="admin-range-picker-popup"
            />
          </div>
          <div className="audit-filter-actions">
            <Button icon={<SearchOutlined />} type="primary" onClick={handleSearch} size="middle" className="admin-btn-primary">
              Search
            </Button>
            <Tooltip title="Clear Filters">
              <Button icon={<ClearOutlined />} onClick={handleClearFilters} size="middle" className="admin-btn-secondary" />
            </Tooltip>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} size="middle" className="admin-btn-secondary" />
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="admin-table-wrapper">
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #e8ecf1', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AuditOutlined style={{ color: '#4f6ef7', fontSize: 13 }} />
          </div>
          <span style={{ fontWeight: 500, fontSize: 13.5, color: '#1a1d26' }}>Audit Trail</span>
          <Badge count={pagination.total} showZero style={{ backgroundColor: '#4f6ef7' }} overflowCount={9999} />
        </div>
        <Table
          dataSource={logs}
          columns={columns}
          loading={loading}
          rowKey={(record) => record.auditId || record.id || Math.random()}
          size="middle"
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => fetchLogs(page, pageSize),
          }}
        />
      </div>

      {/* ── Detail Modal ──────────────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <span>Audit Log Detail</span>
          </Space>
        }
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, log: null })}
        footer={[<Button key="close" onClick={() => setDetailModal({ visible: false, log: null })}>Close</Button>]}
        width={windowWidth < 576 ? '94%' : 700}
        bodyStyle={{ padding: windowWidth < 576 ? '12px' : '16px 24px' }}
        style={{ top: windowWidth < 576 ? 16 : 40 }}
        centered
      >
        {detailModal.log && (
          <div style={{ maxWidth: windowWidth < 576 ? 380 : '100%', margin: '0 auto' }}>
            <Descriptions
              bordered
              column={windowWidth < 576 ? 1 : 2}
              size="small"
              style={{ marginBottom: 16, width: '100%' }}
              labelStyle={windowWidth < 576 ? { width: 108, whiteSpace: 'nowrap', fontSize: 12 } : undefined}
              contentStyle={windowWidth < 576 ? { wordBreak: 'break-word', overflowWrap: 'anywhere' } : undefined}
            >
              <Descriptions.Item label="Action" span={windowWidth < 576 ? 1 : 2}>
                {(() => {
                  const cfg = getActionConfig(detailModal.log.actionType);
                  const label = detailModal.log.actionLabel || cfg.label;
                  return <Tag icon={cfg.icon} color={cfg.color}>{label}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Performed By">
                <Space size={4} style={{ width: '100%' }}>
                  <UserOutlined />
                  <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{detailModal.log.performedBy?.name || 'System'}</span>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Target User">
                <Space size={4} style={{ width: '100%' }}>
                  <TeamOutlined />
                  <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{detailModal.log.target?.name || 'N/A'}</span>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Timestamp" span={windowWidth < 576 ? 1 : 2}>
                <Space size={4} style={{ width: '100%' }}>
                  <ClockCircleOutlined />
                  <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{new Date(detailModal.log.createdAt).toLocaleString()}</span>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={windowWidth < 576 ? 1 : 2}>
                <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{detailModal.log.actionDescription}</span>
              </Descriptions.Item>
              {detailModal.log.ipAddress && (
                <Descriptions.Item label="IP Address">
                  <Space size={4} style={{ width: '100%' }}>
                    <GlobalOutlined />
                    <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{detailModal.log.ipAddress}</span>
                  </Space>
                </Descriptions.Item>
              )}
              {detailModal.log.userAgent && (
                <Descriptions.Item
                  label="User Agent"
                  span={windowWidth < 576 ? 1 : (detailModal.log.ipAddress ? 1 : 2)}
                >
                  <Tooltip title={detailModal.log.userAgent}>
                    <Space size={4} style={{ width: '100%' }}>
                      <LaptopOutlined />
                      <Text
                        ellipsis
                        style={{ maxWidth: windowWidth < 576 ? 200 : 260, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {detailModal.log.userAgent}
                      </Text>
                    </Space>
                  </Tooltip>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider style={{ margin: '12px 0' }}>Change Details</Divider>
            <div style={{ maxWidth: windowWidth < 576 ? 360 : '100%', margin: '0 auto' }}>
              {renderChangeDiff(detailModal.log.oldValue, detailModal.log.newValue)}
            </div>
          </div>
        )}
      </Modal>

      {/* ── User History Modal (actions ON a user) ────────────────────────────── */}
      <Modal
        title={
          <Space>
            <HistoryOutlined style={{ color: '#52c41a' }} />
            <span>Audit History for {userHistoryModal.name}</span>
          </Space>
        }
        open={userHistoryModal.visible}
        onCancel={() => setUserHistoryModal({ visible: false, data: [], loading: false, name: '' })}
        footer={[<Button key="close" onClick={() => setUserHistoryModal({ visible: false, data: [], loading: false, name: '' })}>Close</Button>]}
        width={650}
      >
        <Spin spinning={userHistoryModal.loading}>
          {renderTimeline(userHistoryModal.data)}
        </Spin>
      </Modal>

      {/* ── Admin Actions Modal (actions BY an admin) ─────────────────────────── */}
      <Modal
        title={
          <Space>
            <SafetyOutlined style={{ color: '#722ed1' }} />
            <span>Actions by {adminActionsModal.name}</span>
          </Space>
        }
        open={adminActionsModal.visible}
        onCancel={() => setAdminActionsModal({ visible: false, data: [], loading: false, name: '' })}
        footer={[<Button key="close" onClick={() => setAdminActionsModal({ visible: false, data: [], loading: false, name: '' })}>Close</Button>]}
        width={650}
      >
        <Spin spinning={adminActionsModal.loading}>
          {renderTimeline(adminActionsModal.data)}
        </Spin>
      </Modal>

      {/* ── Role History Modal ────────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <SwapOutlined style={{ color: '#fa8c16' }} />
            <span>Role Change History for {roleHistoryModal.name}</span>
          </Space>
        }
        open={roleHistoryModal.visible}
        onCancel={() => setRoleHistoryModal({ visible: false, data: [], loading: false, name: '' })}
        footer={[<Button key="close" onClick={() => setRoleHistoryModal({ visible: false, data: [], loading: false, name: '' })}>Close</Button>]}
        width={650}
      >
        <Spin spinning={roleHistoryModal.loading}>
          {roleHistoryModal.data.length === 0 && !roleHistoryModal.loading ? (
            <Empty description="No role changes found" />
          ) : (
            <Timeline style={{ marginTop: 16 }}>
              {roleHistoryModal.data.map((item, i) => (
                <Timeline.Item key={item.auditId || i} color="orange" dot={<SwapOutlined />}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 10.5 }}>
                      {new Date(item.createdAt).toLocaleString()}
                    </Text>
                  </div>
                  <Text style={{ fontSize: 12 }}>{item.actionDescription}</Text>
                  {renderChangeDiff(item.oldValue, item.newValue)}
                  {item.performedBy && (
                    <div style={{ marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 10.5 }}>
                        Changed by: {item.performedBy.name || 'System'}
                      </Text>
                    </div>
                  )}
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default AuditLogs;
